<script setup>
  import { connectionStatusApi } from '@/api'
  import { ref, onMounted, onUnmounted } from 'vue'
  import Swal from 'sweetalert2'

  // Estado da conexão
  const isOnline = ref(navigator.onLine)
  const connectionStatus = ref('checking')
  const lastPingTime = ref(0)
  const isChecking = ref(false)
  const lastDowntime = ref(null)
  const lastCheckTime = ref(null)
  const reconnectionAttempts = ref(0)

  // Função para formatar o tempo decorrido
  const formatElapsedTime = startTime => {
    if (!startTime) return '0'
    const elapsed = Date.now() - startTime
    if (elapsed < 60000) return `${Math.floor(elapsed / 1000)} seconds`
    return `${Math.floor(elapsed / 60000)} minutes`
  }

  // Função para completar a animação antes de mostrar o toast
  const showToastCloud = (message, details, type) => {
    const Toast = Swal.mixin({
      toast: true,
      position: 'bottom-end',
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true,
      icon: type,
      title: message,
      showClass: {
        popup: 'swal2-noanimation',
        backdrop: 'swal2-noanimation'
      },
      hideClass: {
        popup: '',
        backdrop: ''
      },
      didOpen: toast => {
        // Criar botão de pause
        const pauseButton = document.createElement('button')
        pauseButton.className = 'btn transparent b-none toast-pause-button'
        pauseButton.innerHTML = '<i class="fa fa-pause"></i>'
        pauseButton.title = 'Pause'
        let isPaused = false

        pauseButton.onclick = () => {
          if (isPaused) {
            Toast.resumeTimer()
            pauseButton.innerHTML = '<i class="fa fa-pause"></i>'
            pauseButton.title = 'Pause'
          } else {
            Toast.stopTimer()
            pauseButton.innerHTML = '<i class="fa fa-play"></i>'
            pauseButton.title = 'Resume'
          }
          isPaused = !isPaused
        }

        // Criar container de ações se não existir
        let actionsContainer = toast.querySelector('.toast-actions')
        if (!actionsContainer) {
          actionsContainer = document.createElement('div')
          actionsContainer.className = 'toast-actions'
          toast.appendChild(actionsContainer)
        }

        // Adicionar botão ao container
        actionsContainer.appendChild(pauseButton)
      },
      html: `
      <div class="connection-toast">
        <span class="connection-details">${details}</span>
      </div>
    `,
      customClass: {
        popup: 'sweet-toast-popup connection-toast-popup',
        title: 'connection-toast-title',
        htmlContainer: 'connection-toast-content'
      }
    })

    Toast.fire()
  }

  // Função para verificar a qualidade da conexão
  const checkConnectionQuality = async () => {
    if (!navigator.onLine) {
      connectionStatus.value = 'poor'
      if (!lastDowntime.value) {
        lastDowntime.value = Date.now()
      }
      return
    }

    isChecking.value = true
    const start = performance.now()
    try {
      const api = new connectionStatusApi()
      await fetch(api.getEndpointUrl(), {
        method: 'GET',
        mode: 'cors'
      })
      const end = performance.now()
      lastPingTime.value = end - start
      lastCheckTime.value = Date.now()

      if (lastPingTime.value < 1000) {
        connectionStatus.value = 'good'
      } else if (lastPingTime.value < 3000) {
        connectionStatus.value = 'medium'
      } else {
        connectionStatus.value = 'poor'
      }

      reconnectionAttempts.value = 0
      lastDowntime.value = null
    } catch (error) {
      console.error('Error checking connection quality:', error)
      connectionStatus.value = 'poor'
      reconnectionAttempts.value++
    } finally {
      setTimeout(() => {
        isChecking.value = false
      }, 1000)
    }
  }

  // Monitora mudanças na conexão
  const handleOnlineStatus = () => {
    isOnline.value = navigator.onLine
    if (isOnline.value) {
      const downtimeMessage = lastDowntime.value
        ? `Downtime duration: ${formatElapsedTime(lastDowntime.value)}`
        : ''
      const message = 'Connection Restored'
      const details = `Syncing pending changes... <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Network status: Online <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> ${downtimeMessage}`
      showToastCloud(message, details, 'success')
      checkConnectionQuality()
    } else {
      lastDowntime.value = Date.now()
      const message = 'Connection Lost'
      const details =
        'Local changes will be synced when back online <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Please check your internet connection <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Network status: Offline'
      showToastCloud(message, details, 'error')
      connectionStatus.value = 'poor'
    }
  }

  // Função para gerar os detalhes do status atual
  const getConnectionDetails = () => {
    const responseTime = `${Math.round(lastPingTime.value)}ms`
    if (!isOnline.value) {
      return {
        message: 'Connection Lost',
        details:
          'Network status: Offline <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Please check your internet connection <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Local changes will be synced when back online',
        type: 'error'
      }
    }

    switch (connectionStatus.value) {
      case 'good':
        return {
          message: 'Excellent Connection',
          details: `Network stability: Optimal <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Perfect for real-time operations <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Response time: ${responseTime}`,
          type: 'success'
        }
      case 'medium':
        return {
          message: 'Moderate Connection',
          details: `Network stability: Fair <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Suitable for most operations <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Response time: ${responseTime}`,
          type: 'warning'
        }
      case 'poor':
        return {
          message: 'Poor Connection',
          details: `Network stability: Unstable <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Some features may be affected <div style="border-bottom: 1px solid currentColor; opacity: 0.1; margin: 4px 0;"></div> Response time: ${responseTime}`,
          type: 'error'
        }
      default:
        return {
          message: 'Checking Connection',
          details: 'Verifying network status...',
          type: 'secondary'
        }
    }
  }

  // Handler para o clique no ícone
  const handleStatusClick = () => {
    const status = getConnectionDetails()
    showToastCloud(status.message, status.details, status.type)
  }

  // Lifecycle hooks
  onMounted(() => {
    window.addEventListener('online', handleOnlineStatus)
    window.addEventListener('offline', handleOnlineStatus)
    checkConnectionQuality() // Verificação inicial

    // Verifica a qualidade da conexão silenciosamente a cada 30 segundos
    const interval = setInterval(() => {
      checkConnectionQuality()
    }, 60000)

    onUnmounted(() => {
      window.removeEventListener('online', handleOnlineStatus)
      window.removeEventListener('offline', handleOnlineStatus)
      clearInterval(interval)
    })
  })
</script>

<template>
  <div class="cloud-icon-container me-2" @click="handleStatusClick">
    <svg
      width="24"
      height="24"
      viewBox="0 0 512 512"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      :class="[
        'cloud-icon',
        {
          'status-good': connectionStatus === 'good' && !isChecking,
          'status-medium': connectionStatus === 'medium' && !isChecking,
          'status-poor': connectionStatus === 'poor' && !isChecking,
          'is-checking': isChecking
        }
      ]"
    >
      <path
        class="cloud-background"
        d="M344.381,143.771C254.765,56.017,102.37,103.776,79.825,227.7c-31.849,4.598-59.138,25.445-72.018,55.076 c-0.016,0.035-0.032,0.07-0.047,0.107c-26.687,61.602,18.784,130.232,85.51,130.232h282.267 c75.246,0,136.463-61.216,136.463-136.462C512,189.241,430.314,123.682,344.381,143.771z"
      />
      <path
        class="cloud-outline"
        d="M375.537,381.12H93.271c-69.246,0-84.534-98.263-18.714-119.456c14.753-4.65,43.01-7.348,74.38,21.892 c6.464,6.024,16.586,5.667,22.61-0.794c6.024-6.464,5.668-16.586-0.794-22.61c-17.93-16.712-38.071-27.33-58.484-31.453 c22.034-99.077,147.374-131.851,215.247-56.305c4.189,4.661,10.714,6.451,16.693,4.57c67.272-21.117,135.795,29.374,135.795,99.69 C480.005,334.256,433.141,381.12,375.537,381.12z"
      />
    </svg>
  </div>
</template>

<style lang="scss" scoped>
  .cloud-icon-container {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
  }

  .cloud-icon {
    width: 24px;
    height: 24px;
    will-change: transform;

    .cloud-background {
      fill: currentColor;
      opacity: 0.7;
    }

    .cloud-outline {
      fill: none;
      stroke: currentColor;
      stroke-width: 5;
      opacity: 0.9;
    }

    &.status-good {
      color: #28a745;
      .cloud-outline {
        animation: outlineGlow 2s ease-in-out infinite;
      }
    }

    &.status-medium {
      color: #ffc107;
      .cloud-outline {
        animation: outlineGlow 2s ease-in-out infinite;
      }
    }

    &.status-poor {
      color: #dc3545;
      .cloud-outline {
        animation: outlineGlow 2s ease-in-out infinite;
      }
    }

    &.is-checking {
      .cloud-outline {
        animation: outlineProgress 2s linear infinite;
        stroke-dasharray: 1000;
        stroke-dashoffset: 1000;
      }
    }
  }

  @keyframes outlineGlow {
    0% {
      filter: drop-shadow(0 0 1px currentColor);
      opacity: 0.7;
    }
    50% {
      filter: drop-shadow(0 0 3px currentColor);
      opacity: 1;
    }
    100% {
      filter: drop-shadow(0 0 1px currentColor);
      opacity: 0.7;
    }
  }

  @keyframes outlineProgress {
    0% {
      stroke-dashoffset: 1000;
    }
    100% {
      stroke-dashoffset: 0;
    }
  }

  // Estilos do Toast
  .connection-toast-popup {
    max-width: 800px !important;
    min-width: 500px !important;
    background: var(--bs-body-bg) !important;
    padding: 1rem !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;

    .connection-toast-title {
      font-size: 1.1em !important;
      margin-bottom: 0.5em !important;
      color: var(--bs-body-color) !important;
      padding-right: 2rem !important;
      font-weight: 600 !important;
    }

    .connection-toast-content {
      padding: 0 !important;
      margin: 0 !important;
    }

    .connection-details {
      margin: 0;
      font-size: 0.95em !important;
      opacity: 0.85;
      color: var(--bs-body-color) !important;
      line-height: 1.5 !important;
    }
  }

  :deep(.toast-actions) {
    display: flex !important;
    justify-content: flex-end !important;
    padding-top: 0.5em !important;
    margin-top: 0.5em !important;
    border-top: 1px solid rgba(0, 0, 0, 0.08) !important;
  }

  :deep(.toast-pause-button) {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: var(--bs-body-color);
    opacity: 0.5;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 !important;

    i {
      font-size: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>
